import type { CircuitState, ManagedCircuit } from "./circuit-breaker";
import { createManagedCircuit } from "./circuit-breaker";

export interface CircuitHealth {
  /** Backward-compatible health field; mirrors dbTransactional. */
  db: CircuitState;
  dbTransactional: CircuitState;
  dbBroadcast: CircuitState;
  dbReporting: CircuitState;
  dbExport: CircuitState;
  redis: CircuitState;
}

const globalForCircuits = globalThis as typeof globalThis & {
  sajilniDbTransactionalCircuit?: ManagedCircuit;
  sajilniDbBroadcastCircuit?: ManagedCircuit;
  sajilniDbReportingCircuit?: ManagedCircuit;
  sajilniDbExportCircuit?: ManagedCircuit;
  sajilniRedisCircuit?: ManagedCircuit;
};

export function getDbTransactionalCircuit(): ManagedCircuit {
  if (!globalForCircuits.sajilniDbTransactionalCircuit) {
    globalForCircuits.sajilniDbTransactionalCircuit = createManagedCircuit({
      name: "db-transactional",
      envPrefix: "DB_TRANSACTIONAL_CIRCUIT",
    });
  }
  return globalForCircuits.sajilniDbTransactionalCircuit;
}

export function getDbBroadcastCircuit(): ManagedCircuit {
  if (!globalForCircuits.sajilniDbBroadcastCircuit) {
    globalForCircuits.sajilniDbBroadcastCircuit = createManagedCircuit({
      name: "db-broadcast",
      envPrefix: "DB_BROADCAST_CIRCUIT",
      defaultTimeout: 10_000,
    });
  }
  return globalForCircuits.sajilniDbBroadcastCircuit;
}

export function getDbReportingCircuit(): ManagedCircuit {
  if (!globalForCircuits.sajilniDbReportingCircuit) {
    globalForCircuits.sajilniDbReportingCircuit = createManagedCircuit({
      name: "db-reporting",
      envPrefix: "DB_REPORTING_CIRCUIT",
      defaultTimeout: 5_000,
    });
  }
  return globalForCircuits.sajilniDbReportingCircuit;
}

export function getDbExportCircuit(): ManagedCircuit {
  if (!globalForCircuits.sajilniDbExportCircuit) {
    globalForCircuits.sajilniDbExportCircuit = createManagedCircuit({
      name: "db-export",
      envPrefix: "DB_EXPORT_CIRCUIT",
      defaultTimeout: 60_000,
      defaultResetTimeout: 60_000,
      defaultVolumeThreshold: 3,
    });
  }
  return globalForCircuits.sajilniDbExportCircuit;
}

export function getRedisCircuit(): ManagedCircuit {
  if (!globalForCircuits.sajilniRedisCircuit) {
    globalForCircuits.sajilniRedisCircuit = createManagedCircuit({
      name: "redis",
    });
  }
  return globalForCircuits.sajilniRedisCircuit;
}

export function getCircuitHealth(): CircuitHealth {
  const dbTransactional = getDbTransactionalCircuit().getState();
  return {
    db: dbTransactional,
    dbTransactional,
    dbBroadcast: getDbBroadcastCircuit().getState(),
    dbReporting: getDbReportingCircuit().getState(),
    dbExport: getDbExportCircuit().getState(),
    redis: getRedisCircuit().getState(),
  };
}

export function getHealthStatusCode(health: CircuitHealth): number {
  return health.dbTransactional === "open" || health.redis === "open"
    ? 503
    : 200;
}

export function isDbCircuitOpen(): boolean {
  return getDbTransactionalCircuit().isOpen();
}

export function isRedisCircuitOpen(): boolean {
  return getRedisCircuit().isOpen();
}

export function withDbTransactionalCircuit<T>(
  fn: () => Promise<T>
): Promise<T> {
  return getDbTransactionalCircuit().fire(fn);
}

export function withDbBroadcastCircuit<T>(fn: () => Promise<T>): Promise<T> {
  return getDbBroadcastCircuit().fire(fn);
}

export function withDbReportingCircuit<T>(fn: () => Promise<T>): Promise<T> {
  return getDbReportingCircuit().fire(fn);
}

export function withDbExportCircuit<T>(fn: () => Promise<T>): Promise<T> {
  return getDbExportCircuit().fire(fn);
}

export function withRedisCircuit<T>(fn: () => Promise<T>): Promise<T> {
  return getRedisCircuit().fire(fn);
}
